/* ============================================================
   push — register / deregister THIS browser for the server-generated digests
   and per-task reminders. The web half of app/src/push.ts, verbatim in spirit:
   register the service worker (public/sw.js, the same file the Expo web target
   ships), `PushManager.subscribe` with the VAPID public key, and POST the
   serialized subscription to the existing `web` push-token route. The backend
   already sends to it (backend/src/adapters/web-push-sender.ts).

   Permission is the caller's concern (SettingsPage asks); a successful
   subscribe implies it was granted.
   ============================================================ */
import type { ApiClient, ServerPushToken, WebPushSubscriptionJSON } from '@habishine/client-core';

import { VAPID_PUBLIC_KEY } from './config';

export type PushResult =
  | { ok: true }
  | { ok: false; reason: 'unsupported' | 'unavailable' | 'denied' | 'error'; message: string };

/** `data.kind` values the backend pushes. */
export type PushKind = 'morning-summary' | 'evening-review' | 'task-reminder';

export interface DigestData {
  kind: PushKind;
  /** notification_sends id — for the fire-and-forget click-through ping. */
  sendId?: string;
  /** the task to open — present only on a task reminder. */
  taskId?: string;
  [k: string]: unknown;
}

const SW_URL = '/sw.js';

/** Is the Push API available in this browser at all? (Safari needs the page to
    be installed to the Dock on macOS < 16; everything else just works.) */
export const pushSupported: boolean =
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  typeof window !== 'undefined' &&
  'PushManager' in window &&
  'Notification' in window;

/** The browser's permission state, or 'unsupported'. */
export function permissionState(): NotificationPermission | 'unsupported' {
  return pushSupported ? Notification.permission : 'unsupported';
}

/** Does this browser currently hold a push subscription? Seeds the Settings
    toggle; the server row is assumed to follow it (re-registering is idempotent). */
export async function isPushRegistered(): Promise<boolean> {
  if (!pushSupported) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return !!sub;
}

/** VAPID keys are base64url; PushManager wants a Uint8Array. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getServiceWorker(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration();
  return existing ?? (await navigator.serviceWorker.register(SW_URL));
}

/** Ask for permission (a no-op when already decided) and register. Idempotent
    server-side, so this is also the silent repair on launch. */
export async function registerForPush(api: ApiClient): Promise<PushResult> {
  try {
    if (!pushSupported) {
      return { ok: false, reason: 'unsupported', message: 'This browser does not support notifications.' };
    }
    if (!VAPID_PUBLIC_KEY) {
      return { ok: false, reason: 'unavailable', message: 'Web push is not configured for this deployment.' };
    }
    const permission =
      Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission;
    if (permission !== 'granted') {
      return { ok: false, reason: 'denied', message: 'Notifications are blocked for this site — allow them in the browser’s site settings.' };
    }
    const reg = await getServiceWorker();
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      }));
    const subscription = sub.toJSON() as WebPushSubscriptionJSON;
    await api.post<ServerPushToken>('/v1/push-tokens', { platform: 'web', subscription });
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: 'error', message: err instanceof Error ? err.message : 'Could not turn on notifications.' };
  }
}

/** Drop the server row and unsubscribe locally. Best-effort. */
export async function deregisterFromPush(api: ApiClient): Promise<void> {
  try {
    if (!pushSupported) return;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    await sub.unsubscribe().catch(() => {});
    await api.del('/v1/push-tokens', { token: endpoint });
  } catch {
    // the server also prunes dead endpoints on the next send
  }
}

/** Bridge the service worker's postMessages to app handlers — `onOpen` when a
    notification is clicked, `onReceived` when one arrives while a tab is
    visible. Returns the unsubscribe. */
export function addDigestListener(handlers: {
  onOpen: (data: DigestData) => void;
  onReceived: (data: DigestData) => void;
}): () => void {
  if (!pushSupported) return () => {};
  const listener = (event: MessageEvent) => {
    const msg = event.data as { type?: string; data?: DigestData } | null;
    if (!msg || typeof msg !== 'object' || !msg.data) return;
    if (msg.type === 'digest-open') handlers.onOpen(msg.data);
    else if (msg.type === 'digest-received') handlers.onReceived(msg.data);
  };
  navigator.serviceWorker.addEventListener('message', listener);
  return () => navigator.serviceWorker.removeEventListener('message', listener);
}
